import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Store, FileText, CreditCard, LogOut, Clock, CheckCircle, AlertCircle, ArrowRight, Zap, Send, DollarSign, BarChart3, Plus, X, CircleDot, Banknote } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { usePiAuth } from "../contexts/PiAuthContext";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-[#D4A017]/20 text-[#D4A017]",
  quoted: "bg-[#3B82F6]/20 text-[#3B82F6]",
  accepted: "bg-[#14B8A6]/20 text-[#14B8A6]",
  paid: "bg-[#2C4A3B]/20 text-[#2C4A3B]",
  completed: "bg-[#2C4A3B] text-white",
  declined: "bg-red-100 text-red-600",
  sent: "bg-[#3B82F6]/20 text-[#3B82F6]",
  payment_initiated: "bg-[#D4A017]/20 text-[#D4A017]",
  awaiting_etransfer: "bg-[#D4A017]/20 text-[#D4A017]",
  awaiting_payment: "bg-[#3B82F6]/20 text-[#3B82F6]",
};

const Dashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const { piUsername } = usePiAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [requestsReceived, setRequestsReceived] = useState([]);
  const [requestsSent, setRequestsSent] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteFormId, setQuoteFormId] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteDesc, setQuoteDesc] = useState("");
  const [quoteItems, setQuoteItems] = useState([{ description: "", amount: "" }]);
  const [quoteSending, setQuoteSending] = useState(false);
  const [myQuotes, setMyQuotes] = useState([]);
  const [sentQuotes, setSentQuotes] = useState({});

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    fetchData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- navigate and fetchData are stable

  const fetchData = async () => {
    try {
      const [reqRes, txnRes] = await Promise.all([
        axios.get(`${API}/commerce/requests`, getAuthHeaders()).catch(() => ({ data: { received: [], sent: [] } })),
        axios.get(`${API}/commerce/transactions`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setRequestsReceived(reqRes.data.received || []);
      setRequestsSent(reqRes.data.sent || []);
      setTransactions(txnRes.data || []);

      // Load quotes for sent requests (customer view)
      const sent = reqRes.data.sent || [];
      const quotedSent = sent.filter(r => ["quoted", "accepted", "completed"].includes(r.status));
      const quotesMap = {};
      await Promise.all(quotedSent.map(async (req) => {
        try {
          const qRes = await axios.get(`${API}/commerce/requests/${req.id}/quotes`);
          if (qRes.data?.length > 0) quotesMap[req.id] = qRes.data[0]; // latest quote
        } catch { /* ignore */ }
      }));
      setSentQuotes(quotesMap);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/commerce/quotes/${quoteId}/respond`, { quote_id: quoteId, action: "accept" });
      toast.success("Quote accepted!");
      fetchData();
    } catch { toast.error("Failed to accept quote"); }
  };

  const handleDeclineQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/commerce/quotes/${quoteId}/respond`, { quote_id: quoteId, action: "decline" });
      toast.success("Quote declined");
      fetchData();
    } catch { toast.error("Failed to decline quote"); }
  };

  const handlePayWithPi = async (quote) => {
    try {
      // First initiate a commerce transaction record
      const txnRes = await axios.post(`${API}/commerce/pay`, {
        quote_id: quote.id,
        payment_method: "pi",
      });
      const txn = txnRes.data;

      await createPiPayment({
        amount: quote.amount,
        memo: `Payment to ${quote.business_name} — ${quote.description?.slice(0, 50)}`,
        metadata: {
          product_type: "commerce",
          transaction_id: txn.id,
          quote_id: quote.id,
          business_name: quote.business_name,
        },
      });
      toast.success("Payment completed!");
      fetchData();
    } catch (err) {
      toast.error(err?.message || "Pi payment failed or was cancelled");
    }
  };

  const handlePayWithEtransfer = async (quote) => {
    try {
      const txnRes = await axios.post(`${API}/commerce/pay`, {
        quote_id: quote.id,
        payment_method: "etransfer",
      });
      toast.success(`E-Transfer instructions: Send $${quote.amount} to gold.manson@gmail.com with ref: ${txnRes.data.id?.slice(0, 8)}`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to initiate payment");
    }
  };

  const handleSendQuote = async (requestId) => {
    const amount = parseFloat(quoteAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (!quoteDesc.trim()) { toast.error("Enter a quote description"); return; }

    const lineItems = quoteItems
      .filter(i => i.description && i.amount)
      .map(i => ({ description: i.description, amount: parseFloat(i.amount) || 0 }));

    setQuoteSending(true);
    try {
      await axios.post(`${API}/commerce/quotes`, {
        request_id: requestId,
        amount,
        description: quoteDesc,
        line_items: lineItems.length > 0 ? lineItems : null,
      }, getAuthHeaders());
      toast.success("Quote sent!");
      setQuoteFormId(null);
      setQuoteAmount("");
      setQuoteDesc("");
      setQuoteItems([{ description: "", amount: "" }]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send quote");
    }
    setQuoteSending(false);
  };

  const addLineItem = () => setQuoteItems([...quoteItems, { description: "", amount: "" }]);
  const removeLineItem = (i) => setQuoteItems(quoteItems.filter((_, idx) => idx !== i));
  const updateLineItem = (i, field, val) => {
    const items = [...quoteItems];
    items[i][field] = val;
    setQuoteItems(items);
    // Auto-calculate total from line items
    const total = items.reduce((s, item) => s + (parseFloat(item.amount) || 0), 0);
    if (total > 0) setQuoteAmount(total.toString());
  };

  const handleLogout = () => { logout(); navigate("/"); };

  if (!user) return null;

  const tierLabel = user.tier === "free" ? "Free" : user.tier?.charAt(0).toUpperCase() + user.tier?.slice(1);
  const isPaid = user.tier && user.tier !== "free";

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO title="Dashboard" description="Your EK Valleys dashboard" />
      <NavbarLight />

      {/* Header */}
      <div className="bg-[#1A2F23] py-8 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white" data-testid="dashboard-title">Welcome, {piUsername ? `@${piUsername}` : user.name}</h1>
            <p className="text-white/60 text-sm mt-1">{piUsername ? `Pi Network · ${user.email || ""}` : user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Badge className={`${isPaid ? "bg-[#14B8A6]" : "bg-[#5C5A52]"} text-white`} data-testid="user-tier-badge">{tierLabel}</Badge>
              {user.is_founding && (
                <Badge className="bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/30" data-testid="user-founding-badge">
                  <Zap className="w-3 h-3 mr-1" />Founding
                </Badge>
              )}
            </div>
            <Button variant="ghost" className="text-white/70 hover:text-white" onClick={handleLogout} data-testid="dashboard-logout">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Upgrade Banner */}
      {!isPaid && (
        <div className="bg-[#D4A017]/10 border-b-2 border-[#D4A017]/30 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-[#D4A017]" />
              <p className="text-sm text-[#1A1915]"><strong>Free tier:</strong> Upgrade to post quotes, access leads, and use the marketplace.</p>
            </div>
            <Link to="/list-business">
              <Button size="sm" className="bg-[#D4A017] hover:bg-[#E5B945] text-[#1A1915] font-semibold" data-testid="upgrade-btn">
                Upgrade <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Tabs + Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6">
          {[
            { id: "requests", label: "Service Requests", icon: Send, count: requestsReceived.length },
            { id: "sent", label: "My Requests", icon: FileText, count: requestsSent.length },
            { id: "transactions", label: "Transactions", icon: DollarSign, count: transactions.length },
          ].map(t => (
            <Button key={t.id} variant={tab === t.id ? "default" : "outline"} className={tab === t.id ? "bg-[#2C4A3B]" : ""}
              onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-2" />{t.label} ({t.count})
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-[#5C5A52] text-center py-12">Loading...</p>
        ) : (
          <>
            {/* Requests Received (as business owner) */}
            {tab === "requests" && (
              <div className="space-y-4">
                {requestsReceived.length === 0 ? (
                  <div className="text-center py-12 bg-white border-2 border-[#D1CFC5] rounded-xl">
                    <Send className="w-10 h-10 text-[#D1CFC5] mx-auto mb-3" />
                    <p className="text-[#5C5A52]">No service requests received yet.</p>
                    <p className="text-xs text-[#5C5A52] mt-1">When customers request your services, they'll appear here.</p>
                  </div>
                ) : requestsReceived.map(req => (
                  <div key={req.id} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid={`request-${req.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-[#1A1915]">{req.requester_name}</p>
                        <p className="text-xs text-[#5C5A52]">{req.requester_email} {req.requester_phone ? `| ${req.requester_phone}` : ""} | {req.service_type}</p>
                      </div>
                      <Badge className={STATUS_COLORS[req.status] || "bg-gray-100"}>{req.status}</Badge>
                    </div>
                    <p className="text-sm text-[#5C5A52] mb-2">{req.description}</p>
                    <div className="flex items-center gap-3 text-xs text-[#5C5A52] mb-3">
                      {req.budget_range && <span>Budget: <strong className="text-[#1A1915]">{req.budget_range}</strong></span>}
                      {req.preferred_date && <span>Preferred: {req.preferred_date}</span>}
                      <span>{req.town} | {new Date(req.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Send Quote Button / Form */}
                    {req.status === "pending" && isPaid && (
                      <>
                        {quoteFormId === req.id ? (
                          <div className="mt-3 border-2 border-[#D4A017]/30 rounded-xl p-4 bg-[#D4A017]/5 space-y-3" data-testid={`quote-form-${req.id}`}>
                            <div className="flex items-center justify-between">
                              <p className="font-semibold text-[#1A1915] text-sm">Create Quote for {req.requester_name}</p>
                              <button onClick={() => setQuoteFormId(null)} className="text-[#5C5A52] hover:text-[#1A1915]"><X className="w-4 h-4" /></button>
                            </div>

                            {/* Line Items */}
                            <div className="space-y-2">
                              <p className="text-xs text-[#5C5A52] font-medium">Line Items (optional)</p>
                              {quoteItems.map((item, i) => (
                <div key={`line-${i}`} className="flex gap-2 items-center">
                                  <input value={item.description} onChange={e => updateLineItem(i, "description", e.target.value)}
                                    placeholder="Item description" className="flex-1 border border-[#D1CFC5] rounded-lg px-3 py-1.5 text-sm bg-white" data-testid={`line-item-desc-${i}`} />
                                  <div className="relative w-28">
                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5A52] text-sm">$</span>
                                    <input value={item.amount} onChange={e => updateLineItem(i, "amount", e.target.value)} type="number" step="0.01" min="0"
                                      placeholder="0.00" className="w-full border border-[#D1CFC5] rounded-lg pl-6 pr-2 py-1.5 text-sm bg-white" data-testid={`line-item-amt-${i}`} />
                                  </div>
                                  {quoteItems.length > 1 && (
                                    <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                                  )}
                                </div>
                              ))}
                              <button type="button" onClick={addLineItem} className="text-xs text-[#2C4A3B] hover:underline flex items-center gap-1" data-testid="add-line-item">
                                <Plus className="w-3 h-3" />Add line item
                              </button>
                            </div>

                            {/* Total + Description */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-[#5C5A52] font-medium">Total Amount *</label>
                                <div className="relative mt-1">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A52]">$</span>
                                  <input value={quoteAmount} onChange={e => setQuoteAmount(e.target.value)} type="number" step="0.01" min="0"
                                    placeholder="0.00" className="w-full border border-[#D1CFC5] rounded-lg pl-7 pr-3 py-2 text-sm bg-white font-semibold" required data-testid="quote-total" />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-[#5C5A52] font-medium">Fee Preview</label>
                                <div className="mt-1 bg-[#EFEDE5] rounded-lg px-3 py-2 text-sm">
                                  {quoteAmount ? (
                                    <>
                                      <span className="text-[#D4A017] font-semibold">Fee: ${(parseFloat(quoteAmount) * 0.04).toFixed(2)}</span>
                                      <span className="text-[#5C5A52] ml-2">Net: ${(parseFloat(quoteAmount) * 0.96).toFixed(2)}</span>
                                    </>
                                  ) : <span className="text-[#5C5A52]">Enter amount</span>}
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="text-xs text-[#5C5A52] font-medium">Quote Description *</label>
                              <textarea value={quoteDesc} onChange={e => setQuoteDesc(e.target.value)} rows={3}
                                placeholder="Describe the work, materials, timeline..."
                                className="w-full mt-1 border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" required data-testid="quote-desc" />
                            </div>

                            <div className="flex gap-2">
                              <Button onClick={() => handleSendQuote(req.id)} disabled={quoteSending}
                                className="flex-1 bg-[#D4A017] hover:bg-[#E5B945] text-[#1A1915] font-semibold" data-testid="send-quote-btn">
                                {quoteSending ? "Sending..." : "Send Quote"}
                              </Button>
                              <Button variant="outline" onClick={() => setQuoteFormId(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" className="mt-2 bg-[#D4A017] hover:bg-[#E5B945] text-[#1A1915] font-semibold"
                            onClick={() => { setQuoteFormId(req.id); setQuoteAmount(""); setQuoteDesc(""); setQuoteItems([{ description: "", amount: "" }]); }}
                            data-testid={`create-quote-${req.id}`}>
                            <Send className="w-3 h-3 mr-1.5" />Send Quote
                          </Button>
                        )}
                      </>
                    )}

                    {req.status === "pending" && !isPaid && (
                      <div className="mt-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-lg p-3 flex items-center justify-between">
                        <p className="text-xs text-[#5C5A52]">Upgrade to a paid plan to send quotes.</p>
                        <Link to="/list-business"><Button size="sm" variant="outline" className="text-xs h-7">Upgrade</Button></Link>
                      </div>
                    )}

                    {(req.status === "quoted" || req.status === "accepted" || req.status === "completed") && (
                      <div className="mt-3 bg-[#F7F6F2] border border-[#D1CFC5] rounded-lg p-3">
                        <p className="text-xs font-medium text-[#2C4A3B]">Quote {req.status === "completed" ? "completed" : req.status}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Requests Sent (as customer) */}
            {tab === "sent" && (
              <div className="space-y-4">
                {requestsSent.length === 0 ? (
                  <div className="text-center py-12 bg-white border-2 border-[#D1CFC5] rounded-xl">
                    <FileText className="w-10 h-10 text-[#D1CFC5] mx-auto mb-3" />
                    <p className="text-[#5C5A52]">You haven&apos;t sent any service requests yet.</p>
                    <Link to="/directory" className="text-sm text-[#2C4A3B] hover:underline mt-2 inline-block">Browse the directory</Link>
                  </div>
                ) : requestsSent.map(req => {
                  const quote = sentQuotes[req.id];
                  return (
                    <div key={req.id} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid={`sent-request-${req.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-[#1A1915]">To: {req.business_name}</p>
                          <p className="text-xs text-[#5C5A52]">{req.service_type} | {new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge className={STATUS_COLORS[req.status] || "bg-gray-100"}>{req.status}</Badge>
                      </div>
                      <p className="text-sm text-[#5C5A52] mb-3">{req.description}</p>

                      {/* Quote received */}
                      {quote && (
                        <div className="mt-3 border-2 border-[#3B82F6]/20 rounded-xl p-4 bg-[#3B82F6]/5">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-semibold text-[#1A1915] text-sm">Quote from {quote.business_name}</p>
                            <Badge className={STATUS_COLORS[quote.status] || "bg-gray-100"}>{quote.status}</Badge>
                          </div>
                          <p className="text-sm text-[#5C5A52] mb-2">{quote.description}</p>
                          <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                            <div><p className="text-[#5C5A52] text-xs">Amount</p><p className="font-bold text-[#1A1915]">${quote.amount?.toLocaleString()}</p></div>
                            <div><p className="text-[#5C5A52] text-xs">Platform Fee</p><p className="text-[#D4A017]">${quote.fee_amount?.toFixed(2)}</p></div>
                            <div><p className="text-[#5C5A52] text-xs">Total</p><p className="font-bold text-[#1A1915]">${quote.amount?.toLocaleString()}</p></div>
                          </div>

                          {/* Accept/Decline buttons */}
                          {quote.status === "sent" && (
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-[#2C4A3B] text-white" onClick={() => handleAcceptQuote(quote.id)} data-testid={`accept-quote-${quote.id}`}>
                                Accept Quote
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-500 border-red-200" onClick={() => handleDeclineQuote(quote.id)} data-testid={`decline-quote-${quote.id}`}>
                                Decline
                              </Button>
                            </div>
                          )}

                          {/* Pay buttons after acceptance */}
                          {quote.status === "accepted" && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-[#2C4A3B]">Quote accepted — choose payment method:</p>
                              <div className="flex gap-2">
                                <Button size="sm" className="bg-[#7B4FC4] hover:bg-[#6B3FA4] text-white" onClick={() => handlePayWithPi(quote)} data-testid={`pay-pi-${quote.id}`}>
                                  <CircleDot className="w-3 h-3 mr-1.5" />Pay with Pi
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handlePayWithEtransfer(quote)} data-testid={`pay-etransfer-${quote.id}`}>
                                  <Banknote className="w-3 h-3 mr-1.5" />E-Transfer
                                </Button>
                              </div>
                            </div>
                          )}

                          {(quote.status === "paid" || quote.status === "payment_initiated") && (
                            <p className="text-xs text-[#2C4A3B] font-medium">Payment in progress</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Transactions */}
            {tab === "transactions" && (
              <div className="space-y-4">
                {transactions.length === 0 ? (
                  <div className="text-center py-12 bg-white border-2 border-[#D1CFC5] rounded-xl">
                    <DollarSign className="w-10 h-10 text-[#D1CFC5] mx-auto mb-3" />
                    <p className="text-[#5C5A52]">No transactions yet.</p>
                  </div>
                ) : transactions.map(txn => (
                  <div key={txn.id} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid={`txn-${txn.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-[#1A1915]">{txn.business_name}</p>
                        <p className="text-xs text-[#5C5A52]">{txn.payment_method} | Ref: {txn.id.slice(0, 8)}</p>
                      </div>
                      <Badge className={STATUS_COLORS[txn.status] || "bg-gray-100"}>{txn.status}</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-[#5C5A52] text-xs">Gross</p>
                        <p className="font-semibold text-[#1A1915]">${txn.gross_amount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[#5C5A52] text-xs">Platform Fee ({txn.fee_percent}%)</p>
                        <p className="font-semibold text-[#D4A017]">${txn.fee_amount?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-[#5C5A52] text-xs">Net to Provider</p>
                        <p className="font-semibold text-[#2C4A3B]">${txn.net_to_provider?.toLocaleString()}</p>
                      </div>
                    </div>
                    {txn.etransfer_instructions && (
                      <div className="mt-3 bg-[#F7F6F2] border border-[#D1CFC5] rounded-lg p-3 text-xs text-[#5C5A52]">
                        {txn.etransfer_instructions}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
